/**
 * useSchedule 훅
 *
 * 책임:
 * - 선택된 날짜의 편성표(/dashboard/schedule?date=…)를 조회
 * - 이전/다음 날 · 오늘 이동 상태 관리
 * - 응답(raw) → 도메인 ScheduleRow[]로 변환 (buildScheduleRows 공유)
 *
 * 대시보드 편성표 보드가 과거 날짜도 볼 수 있도록 useDashboard에서 분리.
 * DashboardContainer에서 사용
 */

import { useEffect, useState, useCallback } from 'react';
import * as endpoints from '../api/endpoints';
import type { ScheduleRow } from '../types';
import { buildScheduleRows } from './scheduleRows';

// 로컬 기준 오늘 날짜 (YYYY-MM-DD)
function todayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// YYYY-MM-DD에 delta일을 더한 YYYY-MM-DD (로컬 기준, 월/연 넘김 처리)
function addDays(dateStr: string, delta: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export interface UseScheduleReturn {
  rows: ScheduleRow[];
  /** 서버가 계산한 현재 시각(0~24). 오늘일 때만 의미 있음. */
  nowHour: number;
  /** 화면에 표시할 날짜 문자열(서버 응답 date, 없으면 선택 날짜). */
  dateLabel: string;
  /** 현재 선택된 날짜 (YYYY-MM-DD). */
  date: string;
  /** 선택 날짜가 오늘인지 — now-line 표시·다음 날 버튼 비활성 판단용. */
  isToday: boolean;
  loading: boolean;
  error: string | null;
  goPrevDay: () => void;
  /** 다음 날 이동. 오늘이면 미래로 못 가므로 무시. */
  goNextDay: () => void;
  goToday: () => void;
  retry: () => void;
}

export function useSchedule(token: string | null): UseScheduleReturn {
  const [date, setDate] = useState<string>(todayStr());
  const [rows, setRows] = useState<ScheduleRow[]>([]);
  const [nowHour, setNowHour] = useState(0);
  const [dateLabel, setDateLabel] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isToday = date === todayStr();

  const fetch = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      // 오늘이면 날짜 파라미터 없이 호출(기존 동작 유지 — 서버가 nowHour 등 기본값 계산).
      const dto = await endpoints.fetchSchedule(date === todayStr() ? undefined : date);
      setRows(buildScheduleRows(dto));
      setNowHour(dto.nowHour);
      setDateLabel(dto.date || date);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token, date]);

  // 날짜 변경 또는 token 변경 시 재조회
  useEffect(() => {
    fetch();
  }, [fetch]);

  const goPrevDay = useCallback(() => setDate((d) => addDays(d, -1)), []);
  const goNextDay = useCallback(
    () => setDate((d) => (d === todayStr() ? d : addDays(d, 1))),
    []
  );
  const goToday = useCallback(() => setDate(todayStr()), []);

  return {
    rows,
    nowHour,
    dateLabel,
    date,
    isToday,
    loading,
    error,
    goPrevDay,
    goNextDay,
    goToday,
    retry: fetch,
  };
}
